import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Camera, ClipboardList, UserPlus, CheckCircle2, ScanLine, FileEdit, Eye, FileCheck2, MailCheck,
  Loader2, Clock, Crosshair, XCircle, Zap, ShieldCheck, UploadCloud,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const QUICK_STEPS = [
  { icon: Camera, title: "SCAN CARD", desc: "Camera extracts details instantly" },
  { icon: ScanLine, title: "AUTO EXTRACT", desc: "AI captures accurate information" },
  { icon: UserPlus, title: "CREATE PROFILE", desc: "Membership form auto-filled for you" },
  { icon: CheckCircle2, title: "REVIEW & SUBMIT", desc: "Verify and submit registration" },
];

const HOW_STEPS = [
  { icon: Camera, title: "SCAN", desc: "Scan the visiting card using your camera." },
  { icon: ScanLine, title: "EXTRACT", desc: "AI reads and extracts card information." },
  { icon: FileEdit, title: "AUTO FILL", desc: "We auto-fill the membership registration form." },
  { icon: Eye, title: "REVIEW", desc: "Review the details and make quick edits if needed." },
  { icon: FileCheck2, title: "SUBMIT", desc: "Submit the form and you're registered." },
  { icon: MailCheck, title: "CONFIRMATION", desc: "You'll receive a confirmation email instantly." },
];

const WHY = [
  { icon: Clock, title: "SAVE TIME", desc: "No manual entry" },
  { icon: Crosshair, title: "HIGH ACCURACY", desc: "AI-powered extraction" },
  { icon: XCircle, title: "REDUCE ERRORS", desc: "Eliminate typing mistakes" },
  { icon: Zap, title: "FAST ONBOARDING", desc: "Get registered in seconds" },
  { icon: ShieldCheck, title: "SMART & SECURE", desc: "Your data is safe with us" },
];

const FIELDS = [
  ["name", "Full Name"], ["designation", "Designation"], ["company", "Company Name"],
  ["mobile", "Mobile Number"], ["email", "Email Address"], ["website", "Website"], ["address", "Address"],
];

const inputCls = "input-dark";
const labelCls = "block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5";

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = () => {
    const img = new Image();
    img.onerror = reject;
    img.onload = () => {
      const max = 1280;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

export default function ScanCard() {
  const fileRef = useRef(null);
  const slotRef = useRef("front");
  const controllerRef = useRef(null);
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [stage, setStage] = useState("scan");
  const [form, setForm] = useState({ name: "", designation: "", company: "", mobile: "", email: "", website: "", address: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const b64 = await fileToBase64(file);
      if (slotRef.current === "back") setBack(b64); else setFront(b64);
    } catch { toast.error("Could not read that image"); }
    e.target.value = "";
  };

  const extract = async () => {
    controllerRef.current = new AbortController();
    setExtracting(true);
    try {
      const { data } = await api.post("/scan/card", { image_base64: front, image_base64_back: back || "" },
        { timeout: 60000, signal: controllerRef.current.signal });
      setForm((f) => ({ ...f, ...data.extracted }));
      setStage("form");
      toast.success("Details extracted — review and submit");
    } catch (err) {
      if (err.name === "CanceledError" || err.code === "ERR_CANCELED") toast.info("Scan stopped");
      else if (err.code === "ECONNABORTED") toast.error("Scan timed out — try a clearer, well-lit photo");
      else toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setExtracting(false); }
  };

  const stopScan = () => controllerRef.current?.abort();

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/scan/register", form);
      setConfirmation(data);
      setStage("done");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const reset = () => { setFront(null); setBack(null); setStage("scan"); setConfirmation(null); setForm({ name: "", designation: "", company: "", mobile: "", email: "", website: "", address: "" }); };

  return (
    <div data-testid="scan-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-800">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(37,99,235,0.14),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20 text-center">
          <p className="gold-kicker mb-4">Connect • Collaborate • Grow</p>
          <h1 className="font-display font-black tracking-tight leading-[1.02]" data-testid="scan-headline">
            <span className="block text-slate-100 text-4xl sm:text-5xl lg:text-6xl">ONE SCAN.</span>
            <span className="block text-gold text-4xl sm:text-5xl lg:text-6xl mt-1">INSTANT CONNECTION.</span>
            <span className="block text-slate-100 text-2xl sm:text-3xl lg:text-4xl mt-2">WE CREATE THE REGISTRATION.</span>
          </h1>
          <p className="mt-5 text-sm md:text-base text-slate-400 max-w-xl mx-auto leading-relaxed">
            Scan any visiting card and we'll automatically capture the details to create a membership registration for you.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-10 max-w-3xl mx-auto" data-testid="scan-quick-steps">
            {QUICK_STEPS.map((s) => (
              <div key={s.title} className="card-surface p-4 text-center">
                <s.icon size={20} className="text-gold mx-auto mb-2.5" />
                <p className="text-[11px] font-bold tracking-wide text-slate-100">{s.title}</p>
                <p className="text-[10.5px] text-slate-500 mt-1 leading-snug">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCAN WIDGET */}
      <section className="max-w-3xl mx-auto px-4 md:px-8 py-16" data-testid="scan-widget">
        {stage === "scan" && (
          <div className="card-surface hairline-gold p-7" data-testid="scan-capture-panel">
            <div className="flex items-center gap-2 mb-5">
              <span className="w-6 h-6 rounded-full bg-gold text-navy-950 font-mono text-xs font-bold flex items-center justify-center">1</span>
              <p className="text-xs font-mono uppercase tracking-widest text-slate-300">Scan Visiting Card</p>
            </div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden" onChange={onFile} data-testid="scan-file-input" />
            <div className="grid sm:grid-cols-2 gap-4">
              {[["front", front, "FRONT SIDE *", "scan-upload-btn", "scan-preview", "scan-retake-btn"],
                ["back", back, "BACK SIDE (OPTIONAL)", "scan-upload-back-btn", "scan-back-preview", "scan-retake-back-btn"]].map(([slot, img, label, upId, pvId, rtId]) => (
                <div key={slot}>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">{label}</p>
                  {!img ? (
                    <button onClick={() => { slotRef.current = slot; fileRef.current?.click(); }} data-testid={upId}
                      className="w-full border-2 border-dashed border-slate-700 hover:border-gold/60 rounded-sm py-10 flex flex-col items-center gap-2.5 transition-colors">
                      <UploadCloud size={24} className="text-gold" />
                      <span className="text-[12px] text-slate-300 font-medium">Capture / upload {slot} side</span>
                    </button>
                  ) : (
                    <div className="relative">
                      <img src={img} alt={`Card ${slot}`} className="w-full h-44 object-contain rounded-sm border border-slate-800 bg-navy-900" data-testid={pvId} />
                      <button onClick={() => (slot === "back" ? setBack(null) : setFront(null))} data-testid={rtId}
                        className="absolute top-2 right-2 text-[10px] font-mono uppercase tracking-widest bg-navy-950/80 border border-slate-700 rounded-sm px-2 py-1 text-slate-300 hover:text-gold transition-colors">Retake</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-600 font-mono mt-3">JPG / PNG / WEBP · Both sides supported — the back side is optional but improves accuracy</p>
            <div className="flex gap-3 mt-4">
              <button onClick={extract} disabled={extracting || !front} data-testid="scan-extract-btn" className="btn-gold flex-1 disabled:opacity-60">
                {extracting ? <><Loader2 size={15} className="animate-spin" /> AI IS READING…</> : <><ScanLine size={15} /> START SCAN</>}
              </button>
              {extracting ? (
                <button onClick={stopScan} data-testid="scan-stop-btn" className="btn-outline !border-red-500/50 !text-red-400">STOP SCAN</button>
              ) : (
                <button onClick={() => { setFront(null); setBack(null); }} data-testid="scan-clear-btn" className="btn-outline">CLEAR</button>
              )}
            </div>
          </div>
        )}

        {stage === "form" && (
          <div className="card-surface hairline-gold p-7 animate-fade-up" data-testid="scan-form-panel">
            <div className="flex items-center gap-2 mb-5">
              <span className="w-6 h-6 rounded-full bg-gold text-navy-950 font-mono text-xs font-bold flex items-center justify-center">2</span>
              <p className="text-xs font-mono uppercase tracking-widest text-slate-300">Review Auto-Filled Registration</p>
            </div>
            <form onSubmit={submit} className="grid md:grid-cols-2 gap-4" data-testid="scan-register-form">
              {FIELDS.map(([key, label]) => (
                <div key={key} className={key === "address" ? "md:col-span-2" : ""}>
                  <label className={labelCls}>{label}{["name", "company", "email"].includes(key) ? " *" : ""}</label>
                  {key === "address"
                    ? <textarea rows={2} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`scan-field-${key}`} className={`${inputCls} resize-none`} />
                    : <input required={["name", "company", "email"].includes(key)} type={key === "email" ? "email" : "text"} value={form[key]}
                        onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`scan-field-${key}`} className={inputCls} />}
                </div>
              ))}
              <div className="md:col-span-2 flex gap-3 mt-2">
                <button type="submit" disabled={submitting} data-testid="scan-submit-btn" className="btn-gold flex-1 disabled:opacity-60">
                  {submitting ? <Loader2 size={15} className="animate-spin" /> : <>SUBMIT REGISTRATION <CheckCircle2 size={15} /></>}
                </button>
                <button type="button" onClick={reset} data-testid="scan-start-over-btn" className="btn-outline">START OVER</button>
              </div>
            </form>
          </div>
        )}

        {stage === "done" && confirmation && (
          <div className="card-surface hairline-gold p-10 text-center animate-fade-up" data-testid="scan-confirmation-panel">
            <CheckCircle2 size={46} className="text-emerald-400 mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold text-emerald-400">Registration Created!</h2>
            <p className="text-sm text-slate-400 mt-2">Your membership registration has been submitted successfully.</p>
            <div className="mt-6 max-w-sm mx-auto space-y-2.5 text-left">
              <div className="flex justify-between border-b border-slate-800 pb-2"><span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Registration ID</span><span className="font-mono text-sm font-bold text-gold" data-testid="scan-registration-id">{confirmation.registration_id}</span></div>
              <div className="flex justify-between border-b border-slate-800 pb-2"><span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Submitted On</span><span className="text-[12px] text-slate-300 font-mono" data-testid="scan-submitted-at">{new Date(confirmation.submitted_at).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Status</span><span className="text-[12px] font-bold text-amber-400" data-testid="scan-status">PENDING APPROVAL</span></div>
            </div>
            <p className="text-[11px] text-slate-600 mt-5">A confirmation email has been sent to your registered email address.</p>
            <div className="flex justify-center gap-3 mt-6">
              <Link to="/" data-testid="scan-home-btn" className="btn-outline">BACK TO HOME</Link>
              <button onClick={reset} data-testid="scan-another-btn" className="btn-gold">SCAN ANOTHER CARD</button>
            </div>
          </div>
        )}
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t border-slate-800 bg-navy-900/40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-center text-slate-100" data-testid="scan-how-title">HOW IT WORKS</h2>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-10" data-testid="scan-how-steps">
            {HOW_STEPS.map((s, i) => (
              <div key={s.title} className="card-surface p-4 text-center relative" data-testid={`scan-how-${i + 1}`}>
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-gold text-navy-950 font-mono text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
                <s.icon size={20} className="text-gold mx-auto mt-2 mb-2.5" />
                <p className="text-[11px] font-bold tracking-wide text-slate-100">{s.title}</p>
                <p className="text-[10.5px] text-slate-500 mt-1.5 leading-snug">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY SCAN & REGISTER */}
      <section className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-center text-slate-100" data-testid="scan-why-title">WHY SCAN & REGISTER?</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-10" data-testid="scan-why-strip">
            {WHY.map((w) => (
              <div key={w.title} className="card-surface p-5 text-center">
                <w.icon size={20} className="text-gold mx-auto mb-2.5" />
                <p className="text-[11px] font-bold tracking-wide text-slate-100">{w.title}</p>
                <p className="text-[10.5px] text-slate-500 mt-1 leading-snug">{w.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-[11px] font-mono text-slate-600 mt-10">SMARTER WAY TO CONNECT. STRONGER WAY TO GROW.</p>
        </div>
      </section>
    </div>
  );
}
